import React, { useState, useEffect, useRef, useCallback } from 'react';
import { messagesAPI, usersAPI } from '../../api';
import {
  MessageCircle, Send, Search, Plus, X, Check, CheckCheck,
  User, Users, ArrowLeft, Loader2, Mail, Inbox
} from 'lucide-react';

/**
 * DirectMessages — Performance Club dark theme + UX overhaul.
 *
 * What's new vs the founder-era version:
 *   - Performance Club dark surface everywhere (matches /, /free, /login,
 *     /register, etc.)
 *   - New Conversation modal now does a real user search (filters by
 *     name/email) instead of asking the user to type a User ID.
 *   - Optimistic send: the message renders instantly with a pending
 *     state, then reconciles with the server response (success or
 *     failure → resend / remove).
 *   - Polling dropped to 3s for the conversation list and 2s for the
 *     active conversation so incoming messages surface fast without
 *     needing a WebSocket.
 *   - Read receipts: own messages get a check / double-check icon and
 *     a "Seen" label once the other participant has read them.
 *   - Keyboard: Enter to send, Shift+Enter for newline.
 *   - Auto-scroll: stays pinned to the latest message unless the user
 *     has scrolled up to read history.
 *
 * What's intentionally not here yet:
 *   - WebSocket push (kept for a follow-up; the 2s polling is
 *     acceptable for the current user volume).
 *   - Typing indicators, attachments, message editing, emoji picker
 *     (none in the founder-era spec, not added now).
 *   - Group chats: backend supports them, UI surfaces them, but the
 *     role chooser in /register only creates direct conversations.
 */
export function DirectMessages({ currentUser }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [totalUnread, setTotalUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showNewConversation, setShowNewConversation] = useState(false);

  // 3s polling for the conversation list — covers new incoming messages
  // even when the user is on a different tab.
  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 3000);
    return () => clearInterval(interval);
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await messagesAPI.getConversations();
      setConversations(response.data.conversations);
      setTotalUnread(response.data.total_unread);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    if (conversation.unread_count > 0) {
      // Mark as read in the background; refresh the list when done.
      messagesAPI.markConversationRead(conversation.id).then(fetchConversations);
    }
  };

  const handleConversationCreated = (conversation) => {
    setConversations((prev) => [conversation, ...prev]);
    setSelectedConversation(conversation);
    setShowNewConversation(false);
  };

  const handleBack = () => {
    setSelectedConversation(null);
    fetchConversations();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12" data-testid="direct-messages-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary-200" />
      </div>
    );
  }

  return (
    <div
      className="h-[calc(100vh-200px)] rounded-2xl border border-[var(--color-border)] bg-[rgba(6,12,10,0.66)] overflow-hidden"
      data-testid="direct-messages"
    >
      <div className="flex h-full">
        {/* Conversation List — hidden on mobile when a conversation is open */}
        <div
          className={`w-full md:w-80 border-r border-[var(--color-border)] flex flex-col ${
            selectedConversation ? 'hidden md:flex' : 'flex'
          }`}
        >
          <div className="p-4 border-b border-[var(--color-border)]">
            <div className="flex items-center justify-between">
              <div>
                <span className="hero-kicker">Direct messages</span>
                <h2 className="mt-1 font-display text-2xl text-[var(--color-text)]">Inbox</h2>
              </div>
              <button
                onClick={() => setShowNewConversation(true)}
                className="p-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-primary-100 hover:bg-white/[0.08] transition-colors"
                data-testid="new-conversation-btn"
                aria-label="Start a new conversation"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            {totalUnread > 0 && (
              <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-primary-200">
                {totalUnread} unread message{totalUnread > 1 ? 's' : ''}
              </p>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <EmptyInbox onStart={() => setShowNewConversation(true)} />
            ) : (
              conversations.map((conversation) => (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  currentUserId={currentUser?.id}
                  isSelected={selectedConversation?.id === conversation.id}
                  onClick={() => handleSelectConversation(conversation)}
                />
              ))
            )}
          </div>
        </div>

        {/* Chat View */}
        <div
          className={`flex-1 flex flex-col ${
            selectedConversation ? 'flex' : 'hidden md:flex'
          }`}
        >
          {selectedConversation ? (
            <ChatView
              key={selectedConversation.id}
              conversation={selectedConversation}
              currentUser={currentUser}
              onBack={handleBack}
            />
          ) : (
            <EmptyChat onStart={() => setShowNewConversation(true)} />
          )}
        </div>
      </div>

      {showNewConversation && (
        <NewConversationModal
          currentUser={currentUser}
          onClose={() => setShowNewConversation(false)}
          onCreated={handleConversationCreated}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Empty states
// --------------------------------------------------------------------------
function EmptyInbox({ onStart }) {
  return (
    <div className="p-8 text-center">
      <div className="mx-auto h-12 w-12 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] flex items-center justify-center mb-3">
        <Inbox className="w-6 h-6 text-[var(--color-text-muted)]" />
      </div>
      <p className="text-sm text-[var(--color-text-muted)]">No conversations yet.</p>
      <button
        onClick={onStart}
        className="mt-3 text-sm text-primary-100 hover:text-primary-200 font-semibold"
        data-testid="empty-inbox-start-btn"
      >
        Start one
      </button>
    </div>
  );
}

function EmptyChat({ onStart }) {
  return (
    <div className="flex-1 flex items-center justify-center text-[var(--color-text-muted)]">
      <div className="text-center">
        <div className="mx-auto h-16 w-16 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] flex items-center justify-center mb-4">
          <MessageCircle className="w-8 h-8 text-[var(--color-text-muted)]" />
        </div>
        <p className="text-sm">Select a conversation, or start a new one.</p>
        <button
          onClick={onStart}
          className="mt-4 px-4 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm hover:bg-white/[0.08] transition-colors"
        >
          New message
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Conversation list item
// --------------------------------------------------------------------------
function ConversationItem({ conversation, currentUserId, isSelected, onClick }) {
  const otherParticipant = conversation.participants.find((p) => p.id !== currentUserId);
  const displayName =
    conversation.conversation_type === 'direct'
      ? otherParticipant?.name
      : conversation.name || 'Group Chat';

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / 86400000);
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const lastMessage = conversation.last_message;
  const unread = conversation.unread_count > 0;

  return (
    <div
      className={`p-4 cursor-pointer transition-colors ${
        isSelected ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]'
      }`}
      onClick={onClick}
      data-testid={`conversation-${conversation.id}`}
    >
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <div className="w-12 h-12 rounded-full border border-[var(--color-border)] bg-white/[0.04] flex items-center justify-center">
            {conversation.conversation_type === 'group' ? (
              <Users className="w-6 h-6 text-primary-100" />
            ) : (
              <span className="text-primary-100 font-display text-lg">
                {displayName?.charAt(0)?.toUpperCase() || '?'}
              </span>
            )}
          </div>
          {unread && (
            <span
              className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-primary-200 text-[#0a1208] text-[10px] font-semibold rounded-full flex items-center justify-center"
              data-testid={`unread-badge-${conversation.id}`}
            >
              {conversation.unread_count}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-baseline">
            <p className={`truncate ${unread ? 'text-[var(--color-text)] font-semibold' : 'text-[var(--color-text-muted)]'}`}>
              {displayName}
            </p>
            {lastMessage && (
              <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)] shrink-0">
                {formatTime(lastMessage.created_at)}
              </span>
            )}
          </div>
          {lastMessage && (
            <p className={`text-sm truncate mt-0.5 ${unread ? 'text-[var(--color-text)]' : 'text-[var(--color-text-muted)]'}`}>
              {lastMessage.sender_id === currentUserId && <span className="opacity-60">You: </span>}
              {lastMessage.content}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Chat view (the active conversation)
// --------------------------------------------------------------------------
function ChatView({ conversation, currentUser, onBack }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const inputRef = useRef(null);
  const wasAtBottomRef = useRef(true);

  const otherParticipant = conversation.participants.find((p) => p.id !== currentUser?.id);
  const displayName =
    conversation.conversation_type === 'direct'
      ? otherParticipant?.name
      : conversation.name || 'Group Chat';

  // 2s polling on the open conversation — surface new messages fast.
  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 2000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.id]);

  // Track whether the user is "at the bottom" — only auto-scroll then.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      wasAtBottomRef.current = distanceFromBottom < 60;
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (wasAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await messagesAPI.getMessages(conversation.id, { limit: 50 });
      setMessages((prev) => {
        // Reconcile: if a message we sent optimistically is now confirmed,
        // replace the optimistic one with the server copy.
        const serverIds = new Set(response.data.map((m) => m.id));
        const optimisticOnly = prev.filter(
          (m) => m.optimistic && !serverIds.has(m.id) && !serverIds.has(m.optimistic_id)
        );
        return [...response.data, ...optimisticOnly];
      });
      setError('');
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Could not load messages.');
    } finally {
      setLoading(false);
    }
  }, [conversation.id]);

  const sendMessage = useCallback(
    async (text) => {
      const tempId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const optimistic = {
        id: tempId,
        optimistic_id: tempId,
        optimistic: true,
        conversation_id: conversation.id,
        sender_id: currentUser?.id,
        sender_name: currentUser?.name,
        content: text,
        created_at: new Date().toISOString(),
        read_by: [currentUser?.id].filter(Boolean),
      };
      setMessages((prev) => [...prev, optimistic]);
      wasAtBottomRef.current = true;
      try {
        const response = await messagesAPI.sendMessage(conversation.id, { content: text });
        setMessages((prev) =>
          prev.map((m) => (m.optimistic_id === tempId ? response.data : m))
        );
      } catch (err) {
        console.error('Error sending message:', err);
        // Mark the optimistic one as failed so the user can retry.
        setMessages((prev) =>
          prev.map((m) =>
            m.optimistic_id === tempId ? { ...m, optimistic_failed: true } : m
          )
        );
      }
    },
    [conversation.id, currentUser]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = newMessage.trim();
    if (!text || sending) return;
    setSending(true);
    setNewMessage('');
    try {
      await sendMessage(text);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
  };

  // Group messages by date.
  const groupedMessages = messages.reduce((groups, message) => {
    const dateStr = new Date(message.created_at).toDateString();
    if (!groups[dateStr]) groups[dateStr] = [];
    groups[dateStr].push(message);
    return groups;
  }, {});

  // Read state per "other" participant — they're "seen" if their id is in
  // read_by (backend field) for every message we sent.
  const ownMessages = messages.filter((m) => m.sender_id === currentUser?.id);
  const lastOwnMessage = ownMessages[ownMessages.length - 1];
  const otherHasReadLast =
    lastOwnMessage &&
    Array.isArray(lastOwnMessage.read_by) &&
    otherParticipant &&
    lastOwnMessage.read_by.includes(otherParticipant.id);

  return (
    <>
      {/* Header */}
      <div className="p-4 border-b border-[var(--color-border)] flex items-center gap-3">
        <button
          onClick={onBack}
          className="md:hidden p-2 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.04]"
          aria-label="Back to conversation list"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="h-10 w-10 rounded-full border border-[var(--color-border)] bg-white/[0.04] flex items-center justify-center">
          {conversation.conversation_type === 'group' ? (
            <Users className="w-5 h-5 text-primary-100" />
          ) : (
            <span className="text-primary-100 font-display">
              {displayName?.charAt(0)?.toUpperCase() || '?'}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[var(--color-text)] truncate">{displayName}</p>
          {conversation.conversation_type === 'group' && (
            <p className="text-xs text-[var(--color-text-muted)]">
              {conversation.participants.length} members
            </p>
          )}
        </div>
        {otherHasReadLast && (
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.22em] text-primary-200"
            data-testid="seen-label"
          >
            Seen
          </span>
        )}
      </div>

      {/* Messages */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
        data-testid="messages-list"
      >
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary-200" />
          </div>
        ) : error ? (
          <div className="border border-red-500/30 bg-red-500/10 text-red-200 p-3 rounded-xl text-sm text-center">
            {error}
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-[var(--color-text-muted)] py-8">
            <p>No messages yet.</p>
            <p className="text-sm mt-1">Say something to start the conversation.</p>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([dateStr, dateMessages]) => (
            <div key={dateStr}>
              <div className="text-center my-4">
                <span className="px-3 py-1 rounded-full border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text-muted)] text-[10px] font-semibold uppercase tracking-[0.22em]">
                  {formatDateHeader(dateMessages[0].created_at)}
                </span>
              </div>
              {dateMessages.map((message, idx) => {
                const isOwn = message.sender_id === currentUser?.id;
                const showAvatar =
                  !isOwn &&
                  (idx === 0 ||
                    dateMessages[idx - 1]?.sender_id !== message.sender_id);
                return (
                  <div
                    key={message.id || message.optimistic_id}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`}
                    data-testid={`message-${message.id || message.optimistic_id}`}
                  >
                    {!isOwn && showAvatar && (
                      <div className="h-8 w-8 rounded-full border border-[var(--color-border)] bg-white/[0.04] flex items-center justify-center mr-2 shrink-0">
                        <span className="text-[var(--color-text-muted)] text-xs font-semibold">
                          {message.sender_name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                    {!isOwn && !showAvatar && <div className="w-10 shrink-0" />}
                    <div
                      className={`max-w-[70%] px-4 py-2 rounded-2xl ${
                        isOwn
                          ? 'bg-primary-200 text-[#0a1208] rounded-br-md'
                          : 'bg-white/[0.06] text-[var(--color-text)] border border-[var(--color-border)] rounded-bl-md'
                      } ${message.optimistic_failed ? 'ring-1 ring-red-500/50' : ''}`}
                    >
                      {!isOwn && conversation.conversation_type === 'group' && showAvatar && (
                        <p className="text-xs font-medium mb-1 text-primary-100">{message.sender_name}</p>
                      )}
                      <p className="whitespace-pre-wrap break-words">{message.content}</p>
                      <div
                        className={`flex items-center justify-end gap-1 mt-1 text-[10px] ${
                          isOwn ? 'text-[#0a1208]/60' : 'text-[var(--color-text-muted)]'
                        }`}
                      >
                        <span>{formatTime(message.created_at)}</span>
                        {isOwn && (
                          message.optimistic_failed ? (
                            <span className="ml-1 text-red-600">Failed</span>
                          ) : message.optimistic ? (
                            <Loader2 className="h-3 w-3 ml-1 animate-spin" />
                          ) : otherHasReadLast && message === lastOwnMessage ? (
                            <CheckCheck className="h-3 w-3 ml-1" />
                          ) : (
                            <Check className="h-3 w-3 ml-1" />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form
        onSubmit={handleSubmit}
        className="p-4 border-t border-[var(--color-border)]"
      >
        <div className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            className="flex-1 px-4 py-2 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]/60 focus:border-primary-200/40 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-200/20 transition-all resize-none max-h-32"
            data-testid="message-input"
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="h-10 w-10 shrink-0 rounded-full bg-gradient-to-r from-primary-500 to-primary-300 text-[#0a1208] flex items-center justify-center hover:from-primary-300 hover:to-primary-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            data-testid="send-message-btn"
            aria-label="Send message"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </>
  );
}

// --------------------------------------------------------------------------
// New Conversation Modal — user search instead of User ID input
// --------------------------------------------------------------------------
function NewConversationModal({ currentUser, onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [error, setError] = useState('');

  // Load all users in the current org once. The backend's /api/users endpoint
  // returns members of the same org (enforced in the router).
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const response = await usersAPI.list();
        if (!mounted) return;
        // Filter out self.
        setUsers((response.data || []).filter((u) => u.id !== currentUser?.id));
      } catch (err) {
        console.error('Error loading users:', err);
        if (mounted) setError('Could not load members.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [currentUser]);

  const filtered = users.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q)
    );
  });

  // If the user typed an email that isn't in the roster, show a
  // "Start chat by email" fallback button. This handles the common
  // "I know their address but they're not showing up in the list"
  // case (e.g. cross-org admins you can't see) — the backend's
  // `/start-with/{user_id}` route resolves by id, so the fallback
  // can either look up the user by email via a small admin endpoint
  // or surface a clear error.
  const isExactEmailMatch =
    search.includes('@') &&
    !filtered.some((u) => (u.email || '').toLowerCase() === search.toLowerCase());

  // Auto-select the only filtered result so the user can hit Enter
  // to start the chat immediately. Cleared the moment they edit
  // the search or pick a different row.
  useEffect(() => {
    if (filtered.length === 1 && filtered[0].id !== selectedUserId) {
      setSelectedUserId(filtered[0].id);
    } else if (filtered.length !== 1 && selectedUserId) {
      // Don't keep a stale selection if it no longer matches the search.
      const stillMatches = filtered.some((u) => u.id === selectedUserId);
      if (!stillMatches) setSelectedUserId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, users.length]);

  const handleStart = async () => {
    if (!selectedUserId || starting) return;
    setStarting(true);
    setError('');
    try {
      const response = await messagesAPI.startConversationWith(selectedUserId);
      onCreated(response.data);
    } catch (err) {
      console.error('Error starting conversation:', err);
      setError('Could not start conversation.');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      data-testid="new-conversation-modal"
    >
      <div className="w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-[rgba(6,12,10,0.92)] overflow-hidden">
        <div className="p-4 border-b border-[var(--color-border)] flex items-center justify-between">
          <div>
            <span className="hero-kicker">New message</span>
            <h2 className="mt-1 font-display text-2xl text-[var(--color-text)]">Pick a member</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.04]"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, or role"
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]/60 focus:border-primary-200/40 focus:outline-none focus:ring-2 focus:ring-primary-200/20"
              data-testid="new-conversation-search"
              autoFocus
            />
          </div>
        </div>

        <div className="max-h-72 overflow-y-auto px-2 pb-2" data-testid="new-conversation-list">
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-primary-200" />
            </div>
          ) : error ? (
            <div className="text-center text-sm text-red-200 py-6">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-sm text-[var(--color-text-muted)] py-6 px-2">
              {users.length === 0 ? (
                'No members in this gym yet.'
              ) : isExactEmailMatch ? (
                <>
                  <p>No member with email <code className="text-primary-100">{search}</code> in your gym.</p>
                  <p className="mt-2 text-xs text-[var(--color-text-muted)]/70">
                    You can only message people in your own org. Ask them to be added, or pick a member from the list above.
                  </p>
                </>
              ) : (
                'No matches.'
              )}
            </div>
          ) : (
            filtered.map((u) => {
              const isSelected = u.id === selectedUserId;
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => setSelectedUserId(u.id)}
                  className={`w-full text-left p-3 rounded-xl flex items-center gap-3 transition-colors ${
                    isSelected
                      ? 'bg-primary-200/10 border border-primary-200/30'
                      : 'hover:bg-white/[0.04] border border-transparent'
                  }`}
                  data-testid={`new-conversation-user-${u.id}`}
                >
                  <div className="h-9 w-9 rounded-full border border-[var(--color-border)] bg-white/[0.04] flex items-center justify-center shrink-0">
                    <span className="text-primary-100 text-sm font-display">
                      {u.name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[var(--color-text)] truncate">{u.name}</p>
                    <p className="text-xs text-[var(--color-text-muted)] truncate">
                      {u.email} {u.role && `· ${u.role}`}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="p-4 border-t border-[var(--color-border)] flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            Cancel
          </button>
          <button
            onClick={handleStart}
            disabled={!selectedUserId || starting}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-primary-500 to-primary-300 text-[#0a1208] text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:from-primary-300 hover:to-primary-200 transition-all"
            data-testid="new-conversation-start"
            title={!selectedUserId ? 'Pick a member first' : undefined}
          >
            {starting ? 'Starting…' : 'Start chat'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DirectMessages;
